import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Plus,
    ExternalLink,
    Truck,
    Loader2,
    Maximize2,
    Flame,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import FocusHeader from "@/components/FocusHeader";
import OrderModal, { URGENCY_META } from "@/components/OrderModal";
import OrderExpandOverlay, { OrderStatusPill } from "@/components/OrderExpandOverlay";

export default function Logistics() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [params, setParams] = useSearchParams();
    const flatRef = useRef([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/logistics/orders");
            setOrders(data);
        } catch (e) {
            toast.error("Failed to load orders.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("logistics", load);

    useEffect(() => {
        load();
    }, []);

    // Deep-link support: /logistics?add=1 opens the add-order modal.
    useEffect(() => {
        if (params.get("add") === "1") {
            setEditOrder(null);
            setShowModal(true);
            params.delete("add");
            setParams(params, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const indexed = useMemo(
        () => orders.map((o, i) => ({ ...o, index: i + 1 })),
        [orders]
    );

    useEffect(() => {
        flatRef.current = indexed;
    }, [indexed]);

    // Voice-triggered expand/collapse for orders.
    useEffect(() => {
        const onExpand = (e) => {
            const n = Number(e.detail?.index);
            const list = flatRef.current;
            if (!n) return;
            const target = list.find((x) => x.index === n);
            if (!target) {
                toast.warning(`Order #${n} not found. Board has ${list.length} orders.`);
                return;
            }
            setExpanded(target);
        };
        const onCollapse = () => setExpanded(null);
        const onOrdersRefreshed = () => load();
        window.addEventListener("virtus:expand-order", onExpand);
        window.addEventListener("virtus:collapse-order", onCollapse);
        window.addEventListener("virtus:orders-refreshed", onOrdersRefreshed);
        return () => {
            window.removeEventListener("virtus:expand-order", onExpand);
            window.removeEventListener("virtus:collapse-order", onCollapse);
            window.removeEventListener("virtus:orders-refreshed", onOrdersRefreshed);
        };
    }, []);

    const saveOrder = async (data) => {
        try {
            if (editOrder?.id) {
                const { data: u } = await api.patch(
                    `/logistics/orders/${editOrder.id}`,
                    data
                );
                setOrders((prev) => prev.map((x) => (x.id === u.id ? u : x)));
            } else {
                const { data: c } = await api.post("/logistics/orders", data);
                setOrders((prev) => [c, ...prev]);
            }
            setShowModal(false);
            toast.success(
                editOrder?.id ? "Order updated." : "Order created + tracking registered."
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const deleteOrder = async () => {
        if (!editOrder?.id) return;
        try {
            await api.delete(`/logistics/orders/${editOrder.id}`);
            setOrders((prev) => prev.filter((x) => x.id !== editOrder.id));
            setShowModal(false);
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    // AfterShip refresh & per-order track removed 2026-02: shipping-status
    // updates now arrive as follow-up emails via the hub@virtus-ai.us webhook.

    // Metrics
    const total = orders.length;
    const inTransit = orders.filter((o) =>
        ["info_received", "in_transit", "out_for_delivery"].includes(o.status)
    ).length;
    const delivered = orders.filter((o) => o.status === "delivered").length;
    const attention = orders.filter((o) =>
        ["exception", "attempt_fail", "expired"].includes(o.status)
    ).length;

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="AO/04"
                title="Logistics"
                subtitle="Part orders · tracking updates auto-ingested from hub@virtus-ai.us."
            />

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-virtus-line flex-shrink-0">
                <Metric label="TOTAL" value={total} color="#FFFFFF" />
                <Metric label="IN TRANSIT" value={inTransit} color="#FFEA00" />
                <Metric label="DELIVERED" value={delivered} color="#00E676" />
                <Metric label="ATTENTION" value={attention} color="#FF1744" />
            </div>

            {/* Action strip — stacks on mobile */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim truncate">
                    ORDERS // {total}
                    <span className="hidden md:inline"> · REQUESTS AUTO-EMAIL <span className="text-virtus-orange">PROCUREMENT</span></span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => {
                            setEditOrder(null);
                            setShowModal(true);
                        }}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                        data-testid={TID.logisticsAddOrderBtn}
                    >
                        <Plus size={11} /> REQUEST ORDER
                    </button>
                </div>
            </div>

            {/* Table — on mobile, allow horizontal swipe since the row grid
                is ~1100px wide. `min-w-[1100px] md:min-w-0` keeps desktop
                fluid but forces overflow on phones. */}
            <div className="flex-1 overflow-auto">
                <div className="min-w-[1100px] md:min-w-0">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : indexed.length === 0 ? (
                    <div className="mx-6 my-8 border border-dashed border-virtus-line p-12 text-center">
                        <Truck size={40} className="mx-auto text-virtus-dim mb-4" />
                        <div className="font-heading text-xl uppercase tracking-tight text-white mb-2">
                            No orders yet
                        </div>
                        <div className="text-sm text-virtus-dim mb-6">
                            Fill out an order request — it lands in this tracker and auto-emails procurement (phils@virtusmaritime.com). Follow-up shipping emails to hub@virtus-ai.us auto-attach the tracking number.
                        </div>
                        <button
                            onClick={() => setShowModal(true)}
                            className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                        >
                            <Plus size={12} className="inline mr-2" /> REQUEST FIRST ORDER
                        </button>
                    </div>
                ) : (
                    <div className="border-t border-virtus-line">
                        {/* Column headers */}
                        <div className="grid grid-cols-[52px_78px_1fr_60px_140px_100px_100px_140px_100px_100px_36px] items-center gap-2 px-4 h-[26px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint sticky top-0 bg-virtus-bg z-10">
                            <div>#</div>
                            <div>URG</div>
                            <div>ITEM</div>
                            <div>QTY</div>
                            <div>VENDOR</div>
                            <div>ORDERED</div>
                            <div>CARRIER</div>
                            <div>TRACKING #</div>
                            <div>STATUS</div>
                            <div>ETA</div>
                            <div />
                        </div>
                        <AnimatePresence>
                            {indexed.map((o) => (
                                <motion.div
                                    key={o.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.12 }}
                                    onClick={() => {
                                        setEditOrder(o);
                                        setShowModal(true);
                                    }}
                                    className="grid grid-cols-[52px_78px_1fr_60px_140px_100px_100px_140px_100px_100px_36px] items-center gap-2 px-4 h-9 border-b border-virtus-line hover:bg-[#141414] cursor-pointer group"
                                    data-testid={TID.logisticsOrderRow(o.id)}
                                >
                                    <div
                                        className="font-mono text-[10px] tracking-[0.2em] font-bold text-virtus-orange"
                                        data-testid={TID.logisticsOrderIndex(o.id)}
                                    >
                                        #{String(o.index).padStart(2, "0")}
                                    </div>
                                    <UrgencyPill value={o.urgency} />
                                    <div className="text-sm text-white group-hover:text-virtus-orange transition-colors truncate flex items-center gap-2">
                                        {o.item}
                                        {o.url && (
                                            <a
                                                href={o.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-virtus-faint hover:text-virtus-orange"
                                                aria-label="Open product URL"
                                            >
                                                <ExternalLink size={11} />
                                            </a>
                                        )}
                                    </div>
                                    <div className="font-mono text-xs text-virtus-orange">
                                        × {o.quantity || 1}
                                    </div>
                                    <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-virtus-dim truncate" title={o.vendor || o.source || "manual"}>
                                        {o.vendor || (o.source || "manual").toUpperCase()}
                                    </div>
                                    <div className="font-mono text-xs text-virtus-dim">
                                        {o.order_date || "—"}
                                    </div>
                                    <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-virtus-dim truncate">
                                        {o.carrier ? (
                                            o.carrier.replace(/[-_]/g, " ")
                                        ) : (
                                            <span className="italic text-virtus-faint tracking-normal normal-case">awaiting</span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[11px] truncate">
                                        {o.tracking_number ? (
                                            <span className="text-virtus-orange">{o.tracking_number}</span>
                                        ) : (
                                            <span className="italic text-virtus-faint">Awaiting shipping email</span>
                                        )}
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <OrderStatusPill value={o.status} />
                                    </div>
                                    <div className="font-mono text-xs text-virtus-dim">
                                        {o.expected_delivery || (
                                            <span className="italic text-virtus-faint">TBD</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setExpanded(o);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 text-virtus-dim hover:text-virtus-orange transition-opacity"
                                            title={`Expand order #${o.index}`}
                                            data-testid={TID.logisticsOrderExpandBtn(o.id)}
                                        >
                                            <Maximize2 size={12} />
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
                </div>
            </div>

            <OrderModal
                open={showModal}
                order={editOrder}
                onClose={() => setShowModal(false)}
                onSave={saveOrder}
                onDelete={deleteOrder}
            />
            <OrderExpandOverlay
                open={!!expanded}
                order={expanded}
                onClose={() => setExpanded(null)}
                onEdit={() => {
                    setEditOrder(expanded);
                    setShowModal(true);
                    setExpanded(null);
                }}
            />
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div className="px-5 py-2 border-r border-virtus-line last:border-r-0 flex items-baseline gap-4">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div
                className="font-mono font-bold text-2xl"
                style={{ color }}
            >
                {String(value).padStart(2, "0")}
            </div>
        </div>
    );
}

function UrgencyPill({ value }) {
    const key = value && URGENCY_META[value] ? value : "standard";
    const meta = URGENCY_META[key];
    const isCritical = key === "critical";
    return (
        <div
            className="inline-flex items-center justify-center gap-1 px-2 h-5 font-mono text-[9px] tracking-[0.22em] uppercase font-bold border w-[64px]"
            style={{
                color: meta.color,
                borderColor: `${meta.color}80`,
                backgroundColor: `${meta.color}18`,
            }}
            data-testid={`logistics-order-urgency-${key}`}
            title={`Urgency: ${meta.label}`}
        >
            {isCritical && <Flame size={9} />}
            {meta.short}
        </div>
    );
}
